import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import s from "./styles.module.scss";
import { ReactComponent as CalendarIcon } from "../../assets/calendaricon.svg";
import { ReactComponent as CoursesIcon } from "../../assets/courses.svg";
import { ReactComponent as ConsoleIcon } from "../../assets/console.svg";
import { ReactComponent as ProfileIcon } from "../../assets/profile.svg";
import { ReactComponent as UploadIcon } from "../../assets/uploadSidebar.svg";
import { ReactComponent as ExitIcon } from "../../assets/logout.svg";
import Modal from "../modal";
import { useSidebar } from "../../contexts/sidebar-context";

// Course addition flow routes
const COURSE_FLOW_ROUTES = [
  "/select-calendar",
  "/choose-sections",
  "/select-sections",
  "/timezone-selection",
  "/add-course-times",
  "/add-events",
  "/events-added",
  "/no-events",
  "/streaming-loading",
  "/user-profile",
];

// Routes that require confirmation before exit
const ROUTES_REQUIRING_CONFIRMATION = [
  "/select-calendar",
  "/choose-sections",
  "/select-sections",
  "/timezone-selection",
  "/add-course-times",
  "/add-events",
];

const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isManuallyCollapsed } = useSidebar();
  const [showExitModal, setShowExitModal] = useState(false);

  // Check if user is in the course addition flow
  const isInCourseFlow = COURSE_FLOW_ROUTES.some(route =>
    pathname === route || pathname.startsWith(route + "/")
  );

  // Check if current route requires exit confirmation
  const requiresConfirmation = ROUTES_REQUIRING_CONFIRMATION.some(route =>
    pathname === route || pathname.startsWith(route + "/")
  );

  // Clear all course flow progress from storage
  const clearCourseFlowProgress = () => {
    localStorage.removeItem("allSectionsData");
    localStorage.removeItem("selectedSections");
    localStorage.removeItem("googleAuthInProgress");
    localStorage.removeItem("selectedCalendarType");
    sessionStorage.removeItem("allSectionsData");
    sessionStorage.removeItem("selectedSections");
  };

  const handleExitClick = () => {
    if (requiresConfirmation) {
      setShowExitModal(true);
    } else {
      navigate("/calendar");
    }
  };

  const handleConfirmExit = () => {
    clearCourseFlowProgress();
    setShowExitModal(false);
    navigate("/calendar");
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  // Regular navigation tabs
  const tabs = [
    { id: "calendar", label: "Calendar", icon: CalendarIcon, path: "/calendar" },
    { id: "courses", label: "Courses", icon: CoursesIcon, path: "/courses" },
    { id: "console", label: "Console", icon: ConsoleIcon, path: "/console" },
    { id: "upload", label: "Upload", icon: UploadIcon, path: "/upload" },
  ];

  // Render exit-only sidebar for course flow
  if (isInCourseFlow) {
    return (
      <>
        <nav className={`${s.sidebar} ${isManuallyCollapsed ? s.collapsed : ''}`}>
          <button
            className={s.exitTab}
            onClick={handleExitClick}
          >
            <ExitIcon className={s.exitIcon} />
            {!isManuallyCollapsed && <span>Exit</span>}
          </button>
        </nav>

        <Modal
          isOpen={showExitModal}
          onClose={handleCancelExit}
          title="Exit course setup?"
          message="You'll lose all progress in the course addition flow. Are you sure you want to exit?"
          type="warning"
          primaryButtonText="Exit"
          secondaryButtonText="Cancel"
          onPrimaryClick={handleConfirmExit}
          onSecondaryClick={handleCancelExit}
        />
      </>
    );
  }

  // Regular sidebar
  return (
    <nav className={`${s.sidebar} ${isManuallyCollapsed ? s.collapsed : ''}`}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = pathname === tab.path ||
          (tab.id === "courses" && pathname.startsWith("/courses"));

        return (
          <button
            key={tab.id}
            className={`${s.tab} ${isActive ? s.active : ""}`}
            onClick={() => navigate(tab.path)}
            title={isManuallyCollapsed ? tab.label : undefined}
            data-tour={`nav-${tab.id}`}
          >
            <Icon className={s.tabIcon} />
            {!isManuallyCollapsed && <span>{tab.label}</span>}
          </button>
        );
      })}
    </nav>
  );
};

export default Sidebar;